import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking and monitoring
 *
 * To use Sentry in production:
 * 1. Create a Sentry account at https://sentry.io/signup/
 * 2. Create a new React project
 * 3. Copy your DSN from the project settings
 * 4. Add VITE_SENTRY_DSN=your_dsn_here to your .env file
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "development";

// Only initialize Sentry if DSN is provided
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production (0.1 = 10% of transactions)
    tracesSampleRate: ENVIRONMENT === "production" ? 0.1 : 1.0,

    // Capture Replay for 10% of all sessions,
    // plus 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Release tracking - uses package.json version
    release: `scient-website@${import.meta.env.VITE_APP_VERSION || "0.0.0"}`,

    // Before sending events, you can filter them
    beforeSend(event, hint) {
      // Don't send events in development unless explicitly enabled
      if (ENVIRONMENT === "development" && !import.meta.env.VITE_SENTRY_DEBUG) {
        if (import.meta.env.DEV) {
          console.error("Sentry Event (not sent in dev):", event, hint);
        }
        return null;
      }
      return event;
    },
  });

  if (import.meta.env.DEV) {
    console.log(`✅ Sentry initialized for ${ENVIRONMENT} environment`);
  }
} else {
  if (import.meta.env.DEV) {
    console.warn(
      "⚠️ Sentry DSN not configured. Error tracking is disabled.\n" +
      "To enable Sentry:\n" +
      "1. Sign up at https://sentry.io/signup/\n" +
      "2. Create a React project\n" +
      "3. Add VITE_SENTRY_DSN=your_dsn_here to your .env file"
    );
  }
}

/**
 * Set user context for Sentry
 * Call this after user authentication
 */
export const setUserContext = (user: {
  id: string;
  email?: string;
  username?: string;
}) => {
  if (SENTRY_DSN) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  }
};

/**
 * Clear user context (on logout)
 */
export const clearUserContext = () => {
  if (SENTRY_DSN) {
    Sentry.setUser(null);
  }
};

/**
 * Manually capture an exception
 */
export const captureException = (error: Error, context?: Record<string, unknown>) => {
  if (SENTRY_DSN) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else if (import.meta.env.DEV) {
    console.error("Error:", error, "Context:", context);
  }
};

/**
 * Manually capture a message
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = "info") => {
  if (SENTRY_DSN) {
    Sentry.captureMessage(message, level);
  } else if (import.meta.env.DEV) {
    console.log(`[${level}]`, message);
  }
};

export default Sentry;
